import { Injectable } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { TemplateObservable } from '@bazis/shared/classes/template-observable';
import { Loader, LoaderOptions } from '@bazis/shared/models/loader.types';

/**
 * **BazisLoaderService**
 *
 * Manages loader instances used by UI to show background activity.
 * Provides methods to create a loader with default options and to dismiss it.
 */
@Injectable({
    providedIn: 'root',
})
export class BazisLoaderService {
    /**
     * Default loader options used for every created loader.
     * Incoming options in `create()` override these defaults.
     */
    private _defaultParams: LoaderOptions = {
        message: '',
    };

    /**
     * Observable holder of active loaders list.
     */
    loaders = new TemplateObservable([]);

    /**
     * Create and register a new loader.
     *
     * A unique `id` is generated with `uuidv4()`.
     *
     * @param params - Partial loader options to apply.
     * @returns Created `Loader` instance.
     */
    create(params: Partial<LoaderOptions>) {
        const loaderParams: LoaderOptions = {
            ...this._defaultParams,
            ...params,
        };

        const id = uuidv4();

        const loader: Loader = {
            options: loaderParams,
            id,
        };

        const loaders = this.loaders._;
        loaders.push(loader);

        this.loaders.set(loaders);

        return loader;
    }

    /**
     * Dismiss a loader and remove it from `loaders` list.
     *
     * @param loader - Loader instance returned by `create()`.
     * @returns void
     */
    dismiss(loader) {
        let loaders = this.loaders._;
        loaders = loaders.filter((v) => v.id !== loader.id);
        this.loaders.set(loaders);
    }
}
